/**
 * Customer-facing summary of the July 2026 signal-quality study.
 * Lives under /markets/test-results. Plain-English only — no formulas,
 * no internal jargon, no promise of returns. Follows the "verbs are
 * open/hold/close/skip/wait/stay" copy rule.
 */
import React, { useEffect } from 'react';
import { Navigation } from '../Navigation';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const BEACON_URL = 'https://icq67h6a7f.execute-api.us-east-2.amazonaws.com/track';
function beacon(page: string) {
  try {
    const source = new URLSearchParams(window.location.search).get('utm_source') || 'organic';
    fetch(BEACON_URL, {
      method: 'POST', mode: 'cors', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page, source, referer: document.referrer || '' }),
    }).catch(() => {});
  } catch { /* never block render */ }
}

const c = {
  bg: '#0f172a', card: '#1f2937', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  blue: '#3b82f6', green: '#22c55e', amber: '#f59e0b',
};

// Public-safe header: renders the full logged-in Navigation for authenticated
// visitors, or a slim brand/sign-in bar for the public visitors (from the
// landing page, IG, or shared link).
const PageHeader: React.FC = () => {
  const { user } = useAuth();
  if (user) return <Navigation />;
  return (
    <nav style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 24px', borderBottom: `1px solid ${c.border}`, background: '#0b1220',
    }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: c.text }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: 4,
          background: c.blue, color: '#fff', fontWeight: 700, fontSize: 14,
        }}>W</span>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Wealth Financial Systems</span>
      </Link>
      <div style={{ display: 'flex', gap: 8 }}>
        <Link to="/login" style={{
          color: c.text, textDecoration: 'none', fontSize: 13, fontWeight: 500,
          padding: '6px 14px', borderRadius: 4, border: `1px solid ${c.border}`,
        }}>Sign in</Link>
        <Link to="/login" style={{
          background: c.blue, color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600,
          padding: '6px 14px', borderRadius: 4,
        }}>Get started</Link>
      </div>
    </nav>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 28 }}>
    <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700 }}>{title}</h2>
    <div style={{ color: c.muted, fontSize: 14, lineHeight: 1.7 }}>{children}</div>
  </div>
);

type PairRow = {
  symbol: string; preset: string; n: number;
  curSum: number; curWin: number;
  fitSum: number; fitWin: number;
};

const money = (n: number) => (n >= 0 ? '+' : '−') + '$' + Math.abs(n).toLocaleString();
const pct = (p: number) => Math.round(p) + '%';

const PAIR_ROWS: PairRow[] = [
  { symbol: 'SPX', preset: 'aggressive',   n: 17, curSum: -24225, curWin: 53,  fitSum: 19415, fitWin: 100 },
  { symbol: 'SPX', preset: 'moderate',     n: 17, curSum: -12770, curWin: 53,  fitSum: 19080, fitWin: 100 },
  { symbol: 'CL',  preset: 'moderate',     n: 14, curSum: -46640, curWin: 0,   fitSum: -21165, fitWin: 7  },
  { symbol: 'ES',  preset: 'aggressive',   n: 13, curSum: -5292,  curWin: 54,  fitSum: 6492,  fitWin: 100 },
  { symbol: 'ES',  preset: 'moderate',     n: 13, curSum: 1345,   curWin: 69,  fitSum: 6492,  fitWin: 100 },
  { symbol: 'CL',  preset: 'aggressive',   n: 14, curSum: -26820, curWin: 0,   fitSum: -23112, fitWin: 14 },
  { symbol: 'SPX', preset: 'conservative', n: 17, curSum: 16612,  curWin: 100, fitSum: 16895, fitWin: 100 },
  { symbol: 'ES',  preset: 'conservative', n: 13, curSum: 6410,   curWin: 100, fitSum: 6492,  fitWin: 100 },
];

type SortKey = 'symbol' | 'preset' | 'n' | 'cur' | 'fit' | 'delta';
type SortDir = 'asc' | 'desc';

const sortValue = (r: PairRow, key: SortKey): number | string => {
  switch (key) {
    case 'symbol': return r.symbol;
    case 'preset': return r.preset;
    case 'n':      return r.n;
    case 'cur':    return r.curSum;
    case 'fit':    return r.fitSum;
    case 'delta':  return r.fitSum - r.curSum;
  }
};

const compare = (a: PairRow, b: PairRow, key: SortKey, dir: SortDir): number => {
  const av = sortValue(a, key); const bv = sortValue(b, key);
  let cmp = 0;
  if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
  else cmp = String(av).localeCompare(String(bv));
  return dir === 'asc' ? cmp : -cmp;
};

const SortHeader: React.FC<{
  label: string;
  align?: 'left' | 'right';
  colKey: SortKey;
  activeKey: SortKey;
  activeDir: SortDir;
  onClick: (k: SortKey) => void;
}> = ({ label, align = 'left', colKey, activeKey, activeDir, onClick }) => {
  const isActive = activeKey === colKey;
  const arrow = isActive ? (activeDir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <th
      onClick={() => onClick(colKey)}
      style={{
        padding: '8px 10px', borderBottom: `1px solid ${c.border}`,
        textAlign: align, color: isActive ? c.text : c.muted, fontWeight: 600,
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      }}
    >
      {label}{arrow}
    </th>
  );
};

const PairTable: React.FC = () => {
  const [sortKey, setSortKey] = React.useState<SortKey>('delta');
  const [sortDir, setSortDir] = React.useState<SortDir>('desc');
  // Numeric columns default to descending on first click, text to ascending;
  // subsequent clicks on the same column flip direction.
  const onSort = (k: SortKey) => {
    if (k === sortKey) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); return; }
    setSortKey(k);
    setSortDir(k === 'symbol' || k === 'preset' ? 'asc' : 'desc');
  };
  const rows = [...PAIR_ROWS].sort((a, b) => compare(a, b, sortKey, sortDir));
  // Highlight the current top-4 in the active sort as the "biggest movers"
  const hiSet = new Set(
    [...PAIR_ROWS].sort((a, b) => compare(a, b, 'delta', 'desc')).slice(0, 4)
      .map((r) => `${r.symbol}#${r.preset}`)
  );
  const td = { padding: '8px 10px', borderBottom: `1px solid ${c.border}` } as React.CSSProperties;
  const num = { ...td, textAlign: 'right', whiteSpace: 'nowrap' } as React.CSSProperties;
  return (
    <div style={{ overflowX: 'auto', marginTop: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 640 }}>
        <thead>
          <tr>
            <SortHeader label="Symbol"  colKey="symbol" activeKey={sortKey} activeDir={sortDir} onClick={onSort} />
            <SortHeader label="Preset"  colKey="preset" activeKey={sortKey} activeDir={sortDir} onClick={onSort} />
            <SortHeader label="n"       align="right" colKey="n"     activeKey={sortKey} activeDir={sortDir} onClick={onSort} />
            <SortHeader label="Current" align="right" colKey="cur"   activeKey={sortKey} activeDir={sortDir} onClick={onSort} />
            <SortHeader label="New"  align="right" colKey="fit"   activeKey={sortKey} activeDir={sortDir} onClick={onSort} />
            <SortHeader label="Δ"       align="right" colKey="delta" activeKey={sortKey} activeDir={sortDir} onClick={onSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const hi = hiSet.has(`${r.symbol}#${r.preset}`);
            const label = { ...td, fontWeight: hi ? 700 : 400 };
            const fitCol = { ...num, color: r.fitSum >= 0 ? c.green : c.text, fontWeight: hi ? 700 : 500 };
            const deltaCol = { ...num, color: c.green, fontWeight: hi ? 700 : 500 };
            return (
              <tr key={`${r.symbol}#${r.preset}`}>
                <td style={label}>{r.symbol}</td>
                <td style={{ ...td, color: c.muted }}>{r.preset}</td>
                <td style={num}>{r.n}</td>
                <td style={{ ...num, color: c.muted }}>{money(r.curSum)} ({pct(r.curWin)})</td>
                <td style={fitCol}>{money(r.fitSum)} ({pct(r.fitWin)})</td>
                <td style={deltaCol}>{money(r.fitSum - r.curSum)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ padding: '10px', borderTop: `2px solid ${c.border}`, fontWeight: 700 }}>Total</td>
            <td style={{ padding: '10px', borderTop: `2px solid ${c.border}`, color: c.muted }}>all presets</td>
            <td style={{ padding: '10px', borderTop: `2px solid ${c.border}`, textAlign: 'right' }}>118</td>
            <td style={{ padding: '10px', borderTop: `2px solid ${c.border}`, textAlign: 'right', color: c.muted, whiteSpace: 'nowrap' }}>−$91,380</td>
            <td style={{ padding: '10px', borderTop: `2px solid ${c.border}`, textAlign: 'right', color: c.green, fontWeight: 700, whiteSpace: 'nowrap' }}>+$30,590</td>
            <td style={{ padding: '10px', borderTop: `2px solid ${c.border}`, textAlign: 'right', color: c.green, fontWeight: 700, whiteSpace: 'nowrap' }}>+$121,970</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export const SignalTestResults: React.FC = () => {
  useEffect(() => { beacon('test-results'); }, []);
  return (
  <div className="dashboard-container" style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
    <PageHeader />
    <div style={{ padding: '24px 32px', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ fontSize: 12, marginBottom: 10 }}>
        <Link to="/" style={{ color: c.muted, textDecoration: 'none' }}>Home</Link>
        <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
        <span style={{ color: c.muted }}>Research</span>
        <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
        <span style={{ color: c.text }}>Research Quality Study</span>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 6, paddingBottom: 10,
        borderBottom: `2px solid ${c.blue}`,
      }}>
        <span style={{ width: 8, height: 28, borderRadius: 2, background: c.blue }} />
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>Research Quality Study</h1>
      </div>
      <div style={{ color: c.dim, fontSize: 12, marginTop: 6 }}>
        Published July 2026 · WFS research desk
      </div>

      <p style={{ marginTop: 22, fontSize: 15, lineHeight: 1.7, color: c.text }}>
        This is a plain read on how our option-picking has been performing —
        what we tested, what we found, and what we changed. If you trade any
        of our short-put picks on <b>ES</b>, <b>SPX</b>, or <b>CL</b>, this
        is the version of the picks you are seeing now.
      </p>

      <Section title="What we tested">
        <p>
          Every pick our system produces has a rank — the number in the RANK
          column on the Notches table. We wanted a simple answer to a simple
          question: <b>do the higher-ranked picks actually do better than the
          lower-ranked ones?</b>
        </p>
        <p>
          We also had a <b>new ranker</b> in the works — same five inputs, but
          the weight on each input was re-tuned against real market history
          instead of the hand-picked values we started with. Before we would
          ever put it live, we wanted proof it did better than the version
          you were seeing.
        </p>
      </Section>

      <Section title="How we tested">
        <p>
          We took every pick from the last few months, walked each one
          forward through the actual hourly option chain, and applied a
          standard exit rule (close at 75% of max profit, stop if the trade
          triples against you, otherwise hold to the DTE window's end).
          That gave us a real P&amp;L number for each pick — not a
          hypothetical, an actual replay of what would have happened.
        </p>
        <p>
          We then compared the version of the ranker you were seeing against
          the new ranker version — pick for pick, on the same picks, with the
          same exit rule. Only picks you could actually trade (45 days to
          expiry or shorter) were counted.
        </p>
      </Section>

      <Section title="How big is the sample">
        <p>
          We surface roughly <b>1,500 top-3 picks per month</b> across every
          symbol Notches covers. This first study only counts a subset of that
          — <b>118 pick-pairs</b>. Two reasons:
        </p>
        <ul style={{ margin: '8px 0 8px 20px', padding: 0, color: c.muted, lineHeight: 1.7 }}>
          <li>
            We only re-scored ES, SPX, and CL under the new ranker. The other
            symbols kept the old scoring, so there was nothing to compare against.
          </li>
          <li>
            We only counted picks that had matured to a full 30-day trade
            window at the time of the study. Anything younger than that had
            no realized P&amp;L yet.
          </li>
        </ul>
        <p>
          That left about 44 unique picks across the three symbols, each
          scored under up to three presets (conservative / moderate /
          aggressive) — 118 pick-pairs total.
        </p>
        <p>
          A sample of 118 is <b>enough to see a strong reading</b> — every
          category improved and none regressed — but it is not the final
          verdict. We will re-run this study in about a month with more
          matured picks to confirm the direction holds.
        </p>
      </Section>

      <Section title="What we found">
        <p>Click any column header to re-sort the table:</p>
        <PairTable />
        <p style={{ marginTop: 14, fontSize: 12, color: c.dim }}>
          n = pick-pairs realized. Win% = share of realized trades that closed for a gain.
          Preset "moderate" is the middle-risk preset (internally called "balanced").
          The four biggest movers stay bold regardless of sort.
        </p>
        <p style={{ marginTop: 16 }}>
          Every category improved or held even — <b>zero got worse</b>. The
          biggest lifts came from SPX and ES aggressive/moderate: batches
          that were 50–70% win rate under the previous version hit 100% in
          the test window under the new ranker. The two conservative categories
          were already at 100% under the previous version, and the new ranker
          kept them there — worth calling out on its own, the new ranker did not
          break trades that were already working.
        </p>
      </Section>

      <Section title="What changed as a result">
        <p>
          The new ranker is now live on <b>ES</b>, <b>SPX</b>, and <b>CL</b>. On
          every other symbol we still use the version you were seeing before,
          because either the new ranker was not clearly better or we did not have
          enough history to trust it yet. Two symbols (NQ, MNQ) were held
          back deliberately — the picks there have been losing regardless of
          which ranker we used, so we are looking at those separately.
        </p>
        <p>
          We also added a hard rule: the ranker will never surface a pick
          more than 45 days to expiry. That matches how the picks are
          actually traded — it keeps you from ever seeing something you would
          not open.
        </p>
      </Section>

      <Section title="What this means for you">
        <p>
          The picks you see on ES, SPX, and CL today are the version that
          our test says has the higher realized P&amp;L. Everything else is
          unchanged. Nothing you have open needs to move. Ranks and scores
          on the Notches table reflect the live ranker.
        </p>
        <p>
          One thing worth saying plainly: this is <em>what happened in the
          past</em>. Forward P&amp;L will not exactly match the numbers in
          the table above — market conditions change, and no ranker is a
          crystal ball. What the test does say is: on the sample we had,
          the new ranker picks the safer trades more often than the version
          before it.
        </p>
      </Section>

      <Section title="What we are watching next">
        <p>
          We will re-run this same study in about a month to see whether
          the new picks continue to outperform in live conditions. If any
          category ever slips backwards, we can flip the ranker back to
          the previous version instantly — no code change required.
        </p>
        <p>
          Separately, we are digging into why NQ and MNQ picks have been
          losing. That is a different problem than ranker weights, and we
          will publish a follow-up when we have an answer.
        </p>
      </Section>

      <div style={{ marginTop: 40, padding: '14px 18px', background: c.card, border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.amber}`, borderRadius: 8 }}>
        <div style={{ color: c.amber, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>Reminder</div>
        <div style={{ fontSize: 13.5, color: c.muted, lineHeight: 1.6 }}>
          Notches picks are information to inform your own trade decisions.
          They are not a recommendation to open, close, or hold any position.
          Past test performance does not guarantee future results.
        </div>
      </div>

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Link to="/notches/dashboard" style={{
          display: 'inline-block',
          background: 'transparent', color: c.blue,
          border: `1px solid ${c.blue}`, padding: '8px 18px', borderRadius: 4,
          fontSize: 13, fontWeight: 600, textDecoration: 'none',
        }}>Back to Notches Dashboard</Link>
      </div>
    </div>
  </div>
  );
};

export default SignalTestResults;
